const TIER_CAPABILITIES = {
  manage: new Set(['job_board', 'crm_contacts', 'crm_lists', 'talent_communities', 'analytics']),
  connect: new Set([
    'job_board',
    'crm_contacts',
    'contacts_import',
    'crm_lists',
    'movements',
    'sharing_contacts_lists',
    'talent_communities',
    'campaigns',
    'analytics',
    'invite_allies',
  ]),
  scale: new Set([
    'job_board',
    'crm_contacts',
    'contacts_import',
    'crm_lists',
    'movements',
    'sharing_contacts_lists',
    'talent_communities',
    'campaigns',
    'people_finder',
    'company_research',
    'analytics',
    'invite_allies',
  ]),
};

const LEGACY_WITH_JOB_BOARD = new Set([
  'job_board',
  'crm_contacts',
  'contacts_import',
  'crm_lists',
  'sharing_contacts_lists',
  'analytics',
  'invite_allies',
]);

const LEGACY_WITHOUT_JOB_BOARD = new Set([
  'crm_contacts',
  'contacts_import',
  'crm_lists',
  'sharing_contacts_lists',
  'analytics',
  'invite_allies',
]);

/**
 * Derives the tier classification from a collection/network object.
 *
 * @param {object} collection - Network object from Redux store (camelCase fields).
 * @returns {{ type: 'new_tier', tier: string } | { type: 'legacy', hasJobBoard: boolean, isPaidConnect: boolean } | { type: 'non_customer' }}
 */
export function deriveTier(collection) {
  if (!collection) {
    return { type: 'non_customer' };
  }

  if (collection.vcBundleTier) {
    return { type: 'new_tier', tier: collection.vcBundleTier };
  }

  if (collection.connect?.hasAccess || collection.connectPremium || collection.connect?.hasPremium) {
    return {
      type: 'legacy',
      hasJobBoard: Boolean(collection.features?.includes('job_board')),
      isPaidConnect: Boolean(collection.connectPremium || collection.connect?.hasPremium),
    };
  }

  // GetroJobs-only: all GJ customers should have free Connect; treat as GJ+GC (3k contact limit).
  if (collection.features?.includes('job_board')) {
    return {
      type: 'legacy',
      hasJobBoard: true,
      isPaidConnect: false,
    };
  }

  return { type: 'non_customer' };
}

/**
 * Checks whether a collection qualifies as a customer (paying or otherwise active).
 *
 * @param {object} collection - Network object from Redux store.
 * @returns {boolean}
 */
export function isCustomer(collection) {
  if (!collection) return false;

  return Boolean(
    collection.chargebeeCustomerId ||
      collection.vcBundleTier ||
      collection.features?.includes('job_board') ||
      collection.connectPremium ||
      collection.connect?.hasPremium ||
      collection.connect?.hasAccess,
  );
}

/**
 * Checks whether a given tier result has a specific capability.
 * Enterprise is treated as Scale for feature visibility.
 *
 * @param {object} tierResult - Output from deriveTier().
 * @param {string} capability - Capability key (e.g. 'contacts_import', 'campaigns').
 * @returns {boolean}
 */
export function hasCapability(tierResult, capability) {
  if (!tierResult) return false;

  if (tierResult.type === 'new_tier') {
    const effectiveTier = tierResult.tier === 'enterprise' ? 'scale' : tierResult.tier;
    const caps = TIER_CAPABILITIES[effectiveTier];
    return caps ? caps.has(capability) : false;
  }

  if (tierResult.type === 'legacy') {
    const caps = tierResult.hasJobBoard ? LEGACY_WITH_JOB_BOARD : LEGACY_WITHOUT_JOB_BOARD;
    return caps.has(capability);
  }

  return false;
}

/**
 * Shortcut: whether the tier result grants access to contacts/connect features.
 * New tiers always have access; legacy requires connect.hasAccess; non-customers do not.
 *
 * @param {object} tierResult - Output from deriveTier().
 * @returns {boolean}
 */
export function hasAccessToContacts(tierResult) {
  if (!tierResult) return false;

  if (tierResult.type === 'new_tier') return true;
  if (tierResult.type === 'legacy') return true;

  return false;
}

/**
 * Whether the network is subject to a contacts limit.
 * Only legacy networks with the connect_premium flag have an enforced cap;
 * new-tier networks (connect, scale, enterprise, manage) never do.
 *
 * @param {object} tierResult - Output from deriveTier().
 * @param {object} collection - Network object from Redux store.
 * @returns {boolean}
 */
export function hasContactsLimit(tierResult, collection) {
  if (!tierResult || !collection) return false;
  return tierResult.type === 'legacy' && !collection.features?.includes('connect_premium');
}
